import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import useTimeState from "hooks/dashboard/useTimeState";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useMemo } from "react";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { dweenColorway } from "utils/constants";
import { colorsSets } from "../sankey-chart/colorsSets";
import { defaultFromDateTime, defaultToDateTime, hexToRgb, shouldShowTime } from "../utils";
import {
  BoundType,
  ConfidenceIntervalLineChartProps,
  XAxisType,
} from "./ConfidenceIntervalLineChartEditor";

const ConfidenceIntervalLineChart = (props: ConfidenceIntervalLineChartProps) => {
  const defaultConfig = useDefaultPlotlyConfig();

  const defaultFromDate = useMemo(
    () => props.from || defaultFromDateTime({ loadConf: props.loadConf }),
    [props.from, props.loadConf]
  );

  const defaultToDate = useMemo(
    () => props.to || defaultToDateTime({ loadConf: props.loadConf }),
    [props.to, props.loadConf]
  );

  const timeState = useTimeState({
    from: props.from || defaultFromDate.toString(),
    to: props.to || defaultToDate.toString(),
  });

  const {
    data: recentData,
    isLoading,
    refetch,
  } = useGetDataInfoRange(
    props.identifiers,
    {
      timeStart:
        timeState.from?.toISOString() || new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      timeEnd: timeState.to?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 10000,
    }
  );

  const plotData = useMemo(() => {
    if (!recentData) return [];

    const result: Partial<Plotly.Data>[] = [];

    recentData.forEach((dataset, index) => {
      if (props.xAxisType === XAxisType.VALUES && index === 0) {
        return;
      }
      const getColor = (i: number) => selectedPalette[i % selectedPalette.length];

      const selectedPalette = colorsSets.find(set => set.label === "Plotly")?.value || [];

      const color = getColor(index);
      
      const fillColor = hexToRgb(color, 0.2);
      
      const groupedData: Record<string, { values: number[] }> = {};

      if (props.xAxisType === XAxisType.TIME) {
        const boundIntervalInSeconds = props.timeBoundIntervalInSeconds ?? 60;
        dataset.data.forEach(point => {
          const timestamp = new Date(point.key);
          const roundedTime =
            Math.floor(timestamp.getTime() / (boundIntervalInSeconds * 1000)) *
            (boundIntervalInSeconds * 1000);
          const roundedKey = new Date(roundedTime).toISOString();

          if (!groupedData[roundedKey]) {
            groupedData[roundedKey] = { values: [] };
          }

          groupedData[roundedKey].values.push(point.value);
        });
      } else if (props.xAxisType === XAxisType.VALUES) {
        const valueBoundsCount = props.valueBoundsCount ?? 5;
        const sortedValues = dataset.data.map(point => point.value).sort((a, b) => a - b);
        const intervalSize =
          (sortedValues[sortedValues.length - 1] - sortedValues[0]) / valueBoundsCount;

        for (let i = 0; i < valueBoundsCount; i++) {
          const lowerBound = sortedValues[0] + i * intervalSize;
          const upperBound = lowerBound + intervalSize;

          const key = `${lowerBound.toFixed(2)}-${upperBound.toFixed(2)}`;

          groupedData[key] = {
            values: dataset.data
              .filter(point => point.value >= lowerBound && point.value < upperBound)
              .map(point => point.value),
          };
        }
      }

      const xValues: (Date | number)[] = [];
      const meanValues: number[] = [];
      const lowerBounds: number[] = [];
      const upperBounds: number[] = [];

      Object.keys(groupedData).forEach(key => {
        const group = groupedData[key];
        const mean = group.values.reduce((sum, val) => sum + val, 0) / group.values.length;

        let lower;
        let upper;

        if (props.boundType === BoundType.MIN_MAX) {
          lower = Math.min(...group.values);
          upper = Math.max(...group.values);
        } else if (props.boundType === BoundType.STD_DEV) {
          const variance =
            group.values.reduce((sum, val) => sum + (val - mean) ** 2, 0) / group.values.length;
          const stdDev = Math.sqrt(variance);
          lower = mean - stdDev;
          upper = mean + stdDev;
        } else if (props.boundType === BoundType.CONFIDENCE) {
          const variance =
            group.values.reduce((sum, val) => sum + (val - mean) ** 2, 0) / group.values.length;
          const stdErr = Math.sqrt(variance) / Math.sqrt(group.values.length);
          const margin = 1.96 * stdErr;
          lower = mean - margin;
          upper = mean + margin;
        }

        if (props.xAxisType === XAxisType.TIME) {
          xValues.push(new Date(key));
        } else {
          const intervalCenter =
            parseFloat(key.split("-")[0]) +
            (parseFloat(key.split("-")[1]) - parseFloat(key.split("-")[0])) / 2;
          xValues.push(intervalCenter);
        }

        meanValues.push(mean);
        if (lower !== undefined) {
          lowerBounds.push(lower);
        }
        if (upper !== undefined) {
          upperBounds.push(upper);
        }
      });

      result.push({
        x: [...xValues, ...xValues.slice().reverse()],
        y: [...upperBounds, ...lowerBounds.slice().reverse()],
        fill: "toself",
        fillcolor: fillColor ?? undefined,
        line: { color: "transparent" },
        name: `${dataset.title} ${T("word.range")}`,
        showlegend: false,
        type: "scatter",
      });

      result.push({
        x: xValues,
        y: meanValues,
        line: { color },
        mode: "lines",
        name: dataset.title,
        type: "scatter",
      });
    });

    return result;
  }, [recentData, props.boundType, props.xAxisType]);

  return (
    <div>
      {!props.hideHeader && (
        <div className="flex flex-row justify-content-between align-items-center">
          {props.showDateSelection && (
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <b className="pl-2">{T("word.from")}</b>
                <CalendarWrapper
                  value={timeState.from}
                  showTime={shouldShowTime(props.loadConf)}
                  onChange={e => timeState.setFrom(e)}
                  dateFormat="yy-mm-dd"
                />
              </div>
              <div className="flex flex-column gap-2">
                <b className="pl-2">{T("word.to")}</b>
                <CalendarWrapper
                  value={timeState.to}
                  showTime={shouldShowTime(props.loadConf)}
                  onChange={e => timeState.setTo(e)}
                  dateFormat="yy-mm-dd"
                />
              </div>
            </div>
          )}
          {!props.hideRefresh && (
            <div className="ml-auto">
              <Button
                icon="pi pi-refresh"
                type="button"
                loading={isLoading}
                onClick={() => refetch()}
                className="p-button-sm"
              />
            </div>
          )}
        </div>
      )}
      {isLoading ? (
        <ProgressSpinner />
      ) : (
        <Plot
          data={plotData}
          config={defaultConfig}
          layout={{
            legend: { orientation: "h" },
            title: props.hideHeader ? undefined : `Line Chart with ${props.boundType}`,
            xaxis: {
              type: props.xAxisType === XAxisType.TIME ? "date" : "linear",
            },
            colorway: dweenColorway
          }}
          style={{ width: "100%", height: "500px" }}
        />
      )}
    </div>
  );
};
export default ConfidenceIntervalLineChart;
